import { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { DraftConfiguration, PrintType } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { getFaqUrl } from '@/utils/faq';
import { t } from '@/utils/i18n';
import { getParams, isCYOMode, isEmbroideryMode } from '@/utils/params';
import { ScreenPrint } from '@/model/printType/printTypes';
import { getIsScreenPrintingAppearance } from '@/model/productType/getIsScreenPrintingAppearance';
import { REFINEMENT_SCREEN_PRINT_ID } from '@/model/productType/labelIds';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { getCurrentAppearance } from '@/redux/draft/getters/getCurrentAppearance';
import { possiblePrintTypesForSelectedConfiguration } from '@/redux/draft/getters/possiblePrintTypesForSelectedConfiguration';
import { getPrintTypes } from '@/redux/printTypes/getters/getPrintTypes';
import { setFilterAction } from '@/redux/productTypes/actions/setFilterAction';
import { TextLink } from '@/components/common/TextLink';
import { ExternalLink } from '@/components/common/icons/ExternalLink';
import { LIST_ROUTE as ProductTypeListRoute } from '@/components/common/product-type-selector/AsyncProductTypeSelector';
import { setPrintTypeAction } from '@/components/draft/draftState/actions/setPrintTypeAction';
import { Badge, BadgeTypes } from './Badge';
import { Button } from './Button';
import { CheckMark } from './icons/CheckMark';
import { Info } from './icons/Info';
import { Minus } from './icons/Minus';
import { Plus } from './icons/Plus';
import './PrintTypeSelector.scss';

export const needsDeactivatedPrintTypeHint = (printType: PrintType) => {
  return getParams().mode === 'admin' && printType.lifeCycleState === 'DEACTIVATED';
};

const WITH_PRINT_TYPE_IMG = new Set(['2', '14', '16', '17']);
const WITH_PRINT_TYPE_INFO = new Set(['2', '5', '14', '16']);

interface ListElementProps {
  printType: { id: string; name: string };
  currentPrintTypeId: string;
  onClick: () => void;
  isDeactivated?: boolean;
}

const ListElement: FunctionComponent<ListElementProps> = ({
  printType,
  currentPrintTypeId,
  onClick,
  isDeactivated,
}) => {
  const [activePrintTypeDetails, setActivePrintTypeDetails] = useState(false);

  const printTypeId = printType.id;
  const isScreenPrinting = printTypeId === ScreenPrint;

  return (
    <li
      className={cn('print-type-selector__list-item', {
        'print-type-selector__list-item--selected': printTypeId === currentPrintTypeId,
        'print-type-selector__list-item--deactivated': isDeactivated,
        'print-type-selector__list-item--screenprinting': isScreenPrinting,
      })}
    >
      <div>
        <button
          type="button"
          onClick={() => setActivePrintTypeDetails(!activePrintTypeDetails)}
          role="option"
          aria-selected={printTypeId === currentPrintTypeId}
          className={cn('print-type-selector__list-button', {
            'print-type-selector__list-button--screenprinting': isScreenPrinting,
          })}
        >
          {printType.name}
          {printTypeId === currentPrintTypeId && (
            <Badge type={BadgeTypes.Selected} className="print-type-selector__list-item--badge">
              {t('printTypePanel.badge.selected')}
            </Badge>
          )}
          {activePrintTypeDetails ? (
            <Minus className="print-type-selector__list-toggle" />
          ) : (
            <Plus className="print-type-selector__list-toggle" />
          )}
        </button>
        {activePrintTypeDetails && (
          <div
            className="print-type-selector__section"
            data-pt={WITH_PRINT_TYPE_IMG.has(printTypeId) ? printTypeId : undefined}
          >
            <ul>
              <li className="print-type-selector__section--list">
                <CheckMark className="print-type-selector__section--icon" />
                {t(`printTypePanel.pt${printTypeId}.listItemOne`)}
              </li>
              <li className="print-type-selector__section--list">
                <CheckMark className="print-type-selector__section--icon" />
                {t(`printTypePanel.pt${printTypeId}.listItemTwo`)}
              </li>
              <li className="print-type-selector__section--list">
                <CheckMark className="print-type-selector__section--icon" />
                {t(`printTypePanel.pt${printTypeId}.listItemThree`)}
              </li>
            </ul>
            <Button
              className="print-type-selector__section--button"
              light
              medium
              disabled={printTypeId === currentPrintTypeId}
              onClick={onClick}
            >
              {printTypeId === currentPrintTypeId
                ? t('printTypePanel.button.selected')
                : t('printTypePanel.button.select')}
            </Button>
            {WITH_PRINT_TYPE_INFO.has(printTypeId) && (
              <div className="print-type-selector__section__info">
                <Info className="print-type-selector__section__info-icon" />
                <div>{t(`printTypePanel.pt${printTypeId}.info`)}</div>
              </div>
            )}
          </div>
        )}
        {isDeactivated ? ' - deactivated' : ''}
      </div>
    </li>
  );
};

interface PrintTypeSelectorProps {
  configuration: DraftConfiguration;
  onClose: () => void;
}

export const PrintTypeSelector: FunctionComponent<PrintTypeSelectorProps> = ({
  configuration,
  onClose,
}) => {
  const dispatch = useReduxDraftDispatch();
  const storeDispatch = useDispatch();
  const handlePrintTypeChange = (printType: PrintType) => {
    trackFeature('print-type-select');
    dispatch(setPrintTypeAction(configuration, printType));
  };

  const currentPrintType = configuration.printType;
  const possiblePrintTypes = useSelector(possiblePrintTypesForSelectedConfiguration);
  const screenPrintListElement = useSelector(getPrintTypes).get(ScreenPrint);
  const navigate = useNavigate();
  const appearance = useSelector(getCurrentAppearance);

  const trackHintClick = () => {
    trackFeature('print-help');
  };

  return (
    <div className="print-type-selector__list">
      <ul role="listbox">
        {possiblePrintTypes.map((pt) => (
          <ListElement
            key={pt.id}
            printType={pt}
            currentPrintTypeId={currentPrintType.id}
            onClick={() => {
              handlePrintTypeChange(pt);
              onClose();
            }}
            isDeactivated={needsDeactivatedPrintTypeHint(pt)}
          />
        ))}
        {screenPrintListElement &&
          isCYOMode() &&
          !isEmbroideryMode() &&
          !getIsScreenPrintingAppearance(appearance) && (
            <ListElement
              printType={screenPrintListElement}
              currentPrintTypeId={currentPrintType.id}
              onClick={() => {
                storeDispatch(setFilterAction({ refinement: REFINEMENT_SCREEN_PRINT_ID }));
                navigate(ProductTypeListRoute);
                onClose();
              }}
            />
          )}
      </ul>
      <TextLink
        className="print-type-selector__hint"
        href={getFaqUrl('printTypes')}
        onClick={trackHintClick}
      >
        <ExternalLink className="print-type-selector__hint-icon" />
        {t('printTypeSelector.hint')}
      </TextLink>
    </div>
  );
};
